import React, { useState } from 'react';
import { SelectUserAvatar } from './SelectUserAvatar';
import { SecureImage } from './SecureImage';

interface AvatarProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  size?: number;
  className?: string;
  src?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  firstName = '',
  lastName = '',
  email = '',
  size = 40,
  className = '',
  src
}) => {
  const [imageError, setImageError] = useState(false);
  const [fallbackError, setFallbackError] = useState(false);

  // If no user data at all, show select user avatar
  const hasUserData = firstName || lastName || email || src;
  if (!hasUserData) {
    return <SelectUserAvatar size={size} className={className} />;
  }

  // Generate initials
  const getInitials = () => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    const initials = first + last;
    
    // If no name data, show a user icon instead of random letters
    if (!initials && !email) {
      return '👤'; // User icon
    }
    
    return initials || email?.charAt(0)?.toUpperCase() || '?';
  };

  // Generate a consistent color based on name
  const getBackgroundColor = () => {
    const name = `${firstName}${lastName}${email}`;
    
    // If no user data, use neutral gray
    if (!name.trim()) {
      return '#9ca3af'; // gray-400
    }
    
    const colors = [
      '#3b82f6', // blue
      '#10b981', // emerald
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#84cc16', // lime
      '#f97316', // orange
      '#ec4899', // pink
      '#6b7280', // gray
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Create SVG avatar with initials
  const createSVGAvatar = () => {
    const initials = getInitials();
    const bgColor = getBackgroundColor();
    const fontSize = Math.max(12, size * 0.4);
    
    const svg = `
      <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${bgColor}"/>
        <text x="${size/2}" y="${size/2 + fontSize/3}" 
              font-family="system-ui, -apple-system, sans-serif" 
              font-size="${fontSize}" 
              font-weight="500"
              fill="white" 
              text-anchor="middle"
              dominant-baseline="middle">
          ${initials}
        </text>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // If we have a custom src and no error, use secure image loading
  if (src && !imageError) {
    return (
      <SecureImage
        src={src}
        alt={`${firstName} ${lastName}`.trim() || 'User avatar'}
        className={`rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
        onError={() => setImageError(true)}
      />
    );
  }

  // Try external avatar service if no custom src or custom src failed
  if (!fallbackError && !imageError) {
    const username = `${firstName} ${lastName}`.trim() || email;
    const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=${getBackgroundColor().replace('#', '')}&color=fff&size=${size}&format=svg`;
    
    return (
      <img
        src={fallbackUrl}
        alt={`${firstName} ${lastName}`.trim() || 'User avatar'}
        className={`rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
        onError={() => setFallbackError(true)}
      />
    );
  }

  // Final fallback: SVG with initials
  return (
    <img
      src={createSVGAvatar()}
      alt={`${firstName} ${lastName}`.trim() || 'User avatar'}
      className={`rounded-full object-cover ${className}`}
      style={{ width: size, height: size }}
    />
  );
}; 